import React, { useState } from 'react';
import {
  Card,
  CardSection,
  SmallHeader,
  Gutter,
  CondensedLarge,
  CondensedHeader,
  CardHeader,
  CandidateDescription,
  TalentExperiences,
  TalentEducation,
  TalentCertifications,
  TalentQualifications,
  TalentLanguages,
  useApi,
  CandidateHighlightExperienceSummary,
  CandidateHighlightExperienceDetails,
  ToastUtil,
  CandidateHighlightsUtil,
  CalendarEventsUtil,
  CalendarPermissionsUtil,
  CalendarTimeZoneUtil,
  LegalTechSkillsDisplay,
  Flashy,
  Icon,
  CardFooter,
} from '@axiom/ui';
import {
  ContactsOpportunitiesConst,
  AccountConst,
  PermissionImpersonationRoles,
  Certification as CertificationConst,
  CandidateOpportunitiesConst,
} from '@axiom/const';
import type {
  AfcSubmissionCandidateOpportunity,
  CalendarEventType,
  CalendarManualInterview,
  CalendarScheduledInterview,
} from '@axiom/validation';
import { useNavigate, Link } from 'react-router-dom';
import { SubmissionCandidateModel } from '@axiom/ui-models';

import { AccountSubmissionsLegacyApi } from '../../api/account-submissions-legacy-api';
import { AccountLegacyApi } from '../../api/account-legacy-api';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { CalendarApi } from '../../api/calendar-api';
import { PublicEngagementToLoginModal } from '../PublicEngagementToLoginModal/PublicEngagementToLoginModal';
import { CandidateCardHeader } from '../CandidateCardHeader/CandidateCardHeader';
import { EngagementPrompt } from '../EngagementPrompt/EngagementPrompt';
import { InstantMatchesApi } from '../../api/instant-matches-api';
import type { EngagementPassModalForm } from '../EngagementPassModal/EngagementPassModal';
import { EngagementPassModal } from '../EngagementPassModal/EngagementPassModal';
import { TalentInterviewScheduleModal } from '../TalentInterviewScheduleModal/TalentInterviewScheduleModal';
import { TalentInterviewConfirmModal } from '../TalentInterviewConfirmModal/TalentInterviewConfirmModal';
import type { TalentInterviewConfirmModalType } from '../../schema/talent-interview-confirm-modal-schema';
import { useEngagementTalentData } from '../../hooks/useEngagementTalentData';
import { CandidateReputation } from '../CandidateReputation/CandidateReputation';
import { TalentInterviewPromptModal } from '../TalentInterviewPromptModal/TalentInterviewPromptModal';
import { ClientRequestApi } from '../../api/client-request-api';
import { SubmissionApi } from '../../api/submission-api';
import type { ClientTogglePinStatus } from '../../schema/client-toggle-pin-status';

import { EngagementTalentMatchChecklist } from './EngagementTalentMatchChecklist';

const showRequestMessage = (
  candidateStatus: AfcSubmissionCandidateOpportunity['candidateStatus']
) => {
  switch (candidateStatus) {
    case CandidateOpportunitiesConst.CandidateStatuses.Selected: {
      return (
        <CardFooter stackableOn="mobile" name="CTA_FOOTER">
          <CondensedLarge name="PENDO_REQUESTED_HIRE">
            <Gutter right="8px" as="span">
              Engagement Requested
            </Gutter>
            <Flashy color="dataVizGreen">
              <Icon name="circle-check-filled" />
            </Flashy>
          </CondensedLarge>
        </CardFooter>
      );
    }
    default: {
      return null;
    }
  }
};

const { QUALIFICATION_AWARDS } = CertificationConst;

export const EngagementTalentDetails = () => {
  const navigate = useNavigate();
  const [displayLoginModal, setDisplayLoginModal] = useState(false);

  const {
    accountId,
    candidateOpp,
    allCandidateOpps,
    submission,
    talentSlug,
    groupedExperiences,
    legalTechSkills,
    user,
  } = useEngagementTalentData();
  const [showPassModal, setShowPassModal] = useState(false);
  const [showEngageConfirmPrompt, setShowEngageConfirmPrompt] = useState<
    boolean | null
  >(false);
  const [showInterviewAuto, setShowInterviewAuto] = useState(false);
  const [showInterviewConfirmModal, setShowInterviewConfirmModal] =
    useState(false);
  const [showInterviewPromptModal, setShowInterviewPromptModal] =
    useState(false);
  const [formattedPrompt, setFormattedPrompt] = useState('');
  const [{ data: calendarData } = { data: undefined }] = useApi(
    candidateOpp?.candidate.calendar?.id
      ? CalendarApi.readCalendar(candidateOpp.candidate.calendar.id)
      : undefined
  );

  if (!submission) {
    throw new Error('Submission not found');
  }
  if (!candidateOpp) {
    throw new Error('Candidate Opportunity not found');
  }

  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());

  const awardTypes = Object.values(QUALIFICATION_AWARDS);
  const calculateExistenceOfCertificationsAndQualifications = (
    candidateOpp.candidate.certifications ?? []
  ).reduce(
    (acc, cert) => {
      if (awardTypes.includes(cert.award)) {
        acc.hasQualifications = true;
      } else {
        acc.hasCertifications = true;
      }
      return acc;
    },
    { hasCertifications: false, hasQualifications: false }
  );
  const { hasCertifications, hasQualifications } =
    calculateExistenceOfCertificationsAndQualifications;

  const handleDataRefresh = async () => {
    return Promise.all([
      AccountLegacyApi.refreshAccount(accountId ?? '', {
        expand: [AccountConst.Expansions.accountTeamUserInfo],
      }),
      SubmissionApi.refreshSubmissionDetails(submission.submissionId),
      AccountSubmissionsLegacyApi.refreshCandidateAccountSubmissionDetails(
        accountId ?? '',
        submission.submissionId,
        candidateOpp.candidate.id
      ),
      InstantMatchesApi.refreshInstantMatches(submission.submissionId),
    ]);
  };

  const handlePinToggle = async () => {
    if (!user) {
      // If no user this must be the public view; send user to login
      setDisplayLoginModal(true);
      return;
    }
    const body: ClientTogglePinStatus = {
      candidateId: candidateOpp.candidate.id,
      opportunityId: submission.id,
      isPinnedByClient: !candidateOpp.pinnedByClient,
    };
    await ClientRequestApi.updatePinnedStatus(body);
    await handleDataRefresh();
  };

  const handlePassClick = () => {
    if (!user) {
      // If no user this must be the public view; send user to login
      setDisplayLoginModal(true);
      return;
    }

    setShowPassModal(!showPassModal);
  };

  const handleClosePassModal = () => {
    setShowPassModal(!showPassModal);
  };

  const handleAfterPassedOrInterviewNavigation = () => {
    const allCandidateIds = allCandidateOpps.map(
      (item: AfcSubmissionCandidateOpportunity) => item.candidate.id
    );

    const talentIndex = allCandidateIds.indexOf(candidateOpp.candidate.id);
    const nonPassedTalent = [...allCandidateIds];
    nonPassedTalent.splice(talentIndex, 1);

    let nextTalent: string | null | undefined = null;

    if (nonPassedTalent.length > 0) {
      nextTalent =
        talentIndex === allCandidateIds.length - 1
          ? allCandidateIds[talentIndex - 1]
          : allCandidateIds[talentIndex + 1];
    }

    if (nextTalent) {
      const to =
        talentIndex === 0 && !talentSlug
          ? `./${nextTalent}`
          : `../${nextTalent}`;
      navigate(to);
    }
  };

  const handleSubmitPassModal = async (formData: EngagementPassModalForm) => {
    handleClosePassModal();
    await (candidateOpp.isInstantMatch
      ? InstantMatchesApi.updateInstantMatchPassed({
          candidateId: candidateOpp.candidate.id,
          opportunityId: submission.id,
        })
      : ClientRequestApi.createPassOnCandidateRequest({
          submissionId: submission.submissionId,
          candidateId: candidateOpp.candidate.id,
          rejectionLossCodes: [formData.rejectionLossCode],
          notes: formData.notes,
        }));
    await handleDataRefresh();
    handleAfterPassedOrInterviewNavigation();
  };

  const handleInterviewRequestClick = () => {
    if (!user) {
      // If no user this must be the public view; send user to login
      setDisplayLoginModal(true);
      return;
    }

    const calendarPermissions = CalendarPermissionsUtil(
      user,
      PermissionImpersonationRoles.client
    );
    const hasFreeTime = calendarData?.events?.find(event => {
      return (
        !event.busy &&
        !CalendarEventsUtil.isInThePast(
          event.startTime,
          calendarPermissions.userPermissions()
        )
      );
    });

    if (hasFreeTime) {
      setShowInterviewAuto(true);
    } else {
      setShowInterviewPromptModal(true);
    }
  };

  const loadToast = () => {
    ToastUtil.add({
      name: `INTERVIEW_REQUESTED`,
      type: 'info',
      children: (
        <>
          <CondensedHeader>Interview Requested</CondensedHeader>
          <CondensedLarge>
            We have added this match to the{' '}
            <Link
              data-test="TO_INTERVIEW_TAB"
              to={`${window.location.pathname.split('matches')[0]}interviews`}
            >
              Interviews tab
            </Link>{' '}
            and will confirm with everyone when the interview is set up.
          </CondensedLarge>
        </>
      ),
    });
  };

  const loadFailureToast = () => {
    ToastUtil.add({
      name: `FAILED_INTERVIEW_OR_ENGAGE_REQUEST`,
      type: 'error',
      dismissible: true,
      children: (
        <>
          <CondensedHeader>Error - Please refresh your browser</CondensedHeader>
          <CondensedLarge>
            Looks like there was an issue updating some of the data on this
            page.{' '}
            <Link
              data-test="REFRESH_LINK"
              to="#"
              onClick={e => {
                e.preventDefault();
                SubmissionApi.refreshSubmissionDetails(submission.submissionId)
                  .then(() => {
                    ToastUtil.removeAll();
                  })
                  .catch(() => {});
              }}
            >
              Refresh
            </Link>
          </CondensedLarge>
        </>
      ),
    });
  };

  const handleEngageRequestToggle = () => {
    if (!user) {
      // If no user this must be the public view; send user to login
      setDisplayLoginModal(true);
      return;
    }

    setShowEngageConfirmPrompt(!showEngageConfirmPrompt);
  };

  const handleEngageConfirm = async () => {
    setShowEngageConfirmPrompt(null);
    try {
      await ClientRequestApi.createEngagementRequest({
        submissionId: submission.submissionId,
        candidateId: candidateOpp.candidate.id,
      });
      await handleDataRefresh();
    } catch {
      loadFailureToast();
    }
  };

  const confirmInterview = async (
    data?: TalentInterviewConfirmModalType
  ): Promise<unknown> => {
    setShowInterviewConfirmModal(false);
    const payload = {
      adhocAttendees: data?.adHocContacts,
      contactIds: data?.contacts,
      preferredVideoClient: data?.isZoomNotPreferred
        ? null
        : AccountConst.PreferredVideoClient.zoom,
      candidateId: candidateOpp.candidate.id,
      submissionId: submission.submissionId,
      availabilityNote: data?.availabilityNote,
    } as CalendarManualInterview;

    try {
      const res = await CalendarApi.createManualInterview(payload);
      await handleDataRefresh();

      loadToast();

      return res;
    } catch {
      loadFailureToast();
      return;
    } finally {
      setShowInterviewAuto(false);
    }
  };

  const scheduleInterview = async (
    data?: TalentInterviewConfirmModalType,
    scheduledEvent?: CalendarEventType
  ): Promise<unknown> => {
    const payload = {
      adhocAttendees: data?.adHocContacts,
      contactIds: data?.contacts,
      preferredVideoClient: data?.isZoomNotPreferred
        ? null
        : AccountConst.PreferredVideoClient.zoom,
      candidateId: candidateOpp.candidate.id,
      submissionId: submission.submissionId,
      title: scheduledEvent?.title,
      startTime: scheduledEvent?.startTime,
      endTime: scheduledEvent?.endTime,
      busy: scheduledEvent?.busy,
    } as CalendarScheduledInterview;

    try {
      const res = await CalendarApi.createScheduledCalendarInterview(payload);
      await handleDataRefresh();
      loadToast();
      return res;
    } catch {
      loadFailureToast();
      return;
    } finally {
      setShowInterviewAuto(false);
    }
  };

  const candidateDescription = new SubmissionCandidateModel(
    candidateOpp
  ).calculatedDescription();

  const primaryContactId = submission.contacts?.find(
    contact => contact.displayRole === ContactsOpportunitiesConst.Roles.Owner
  )?.id;

  return (
    <div data-test="talent-details">
      <Card>
        <CardHeader>
          <CandidateCardHeader
            showContextMenu
            candidateOpp={candidateOpp}
            onInterview={handleInterviewRequestClick}
            submission={submission}
            onPass={handlePassClick}
            onEngage={handleEngageRequestToggle}
            updatePinnedStatus={handlePinToggle}
          />
        </CardHeader>
        {!!candidateDescription && (
          <CardSection divider>
            <CandidateDescription
              description={candidateDescription}
              calculatedFirstName={candidateOpp.candidate.calculatedFirstName}
            />
            {submission.selfService ? (
              <EngagementTalentMatchChecklist
                candidate={candidateOpp.candidate}
                legalTechSkills={legalTechSkills}
                submission={submission}
                taxonomy={taxonomy}
              />
            ) : null}
          </CardSection>
        )}

        {showRequestMessage(candidateOpp.candidateStatus)}
      </Card>
      <Gutter bottom="16px" />
      {CandidateHighlightsUtil.calculateNumberOfHighlights(
        candidateOpp.candidate,
        submission
      ) > 0 && (
        <Card>
          <CardHeader>
            <SmallHeader>Highlights</SmallHeader>
          </CardHeader>
          <CardSection>
            <CandidateHighlightExperienceSummary
              candidate={candidateOpp.candidate}
              submission={submission}
            />
            <Gutter top="16px">
              <CandidateHighlightExperienceDetails
                candidate={candidateOpp.candidate}
                taxonomy={taxonomy}
              />
            </Gutter>
          </CardSection>
        </Card>
      )}
      <Gutter bottom="16px" />
      <CandidateReputation candidateOpp={candidateOpp} />
      <Gutter bottom="16px" />
      {groupedExperiences.length > 0 && (
        <Gutter bottom="16px">
          <Card>
            <CardHeader>
              <SmallHeader>Experience</SmallHeader>
            </CardHeader>
            <TalentExperiences groupedExperiences={groupedExperiences} />
          </Card>
        </Gutter>
      )}
      {legalTechSkills.length > 0 && (
        <Gutter bottom="16px">
          <Card name="degrees">
            <CardHeader>
              <SmallHeader>Legal Tech Skills</SmallHeader>
            </CardHeader>
            <CardSection>
              <LegalTechSkillsDisplay
                legalTechSkills={legalTechSkills}
                taxonomy={taxonomy}
              />
            </CardSection>
          </Card>
        </Gutter>
      )}
      {(candidateOpp.candidate.degrees?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card name="degrees">
            <CardHeader>
              <SmallHeader>Education</SmallHeader>
            </CardHeader>
            <CardSection>
              <TalentEducation candidate={candidateOpp.candidate} />
            </CardSection>
          </Card>
        </Gutter>
      )}
      {(candidateOpp.candidate.certifications?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card name="certifications">
            <CardHeader>
              <SmallHeader>Accomplishments</SmallHeader>
            </CardHeader>
            {hasCertifications ? (
              <CardSection divider name="certifications-subsection">
                <TalentCertifications candidate={candidateOpp.candidate} />
              </CardSection>
            ) : null}
            {hasQualifications ? (
              <CardSection name="qualifications-subsection">
                <TalentQualifications candidate={candidateOpp.candidate} />
              </CardSection>
            ) : null}
          </Card>
        </Gutter>
      )}
      {(candidateOpp.candidate.languages?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card>
            <CardHeader>
              <SmallHeader>Languages</SmallHeader>
            </CardHeader>
            <CardSection>
              <TalentLanguages candidate={candidateOpp.candidate} />
            </CardSection>
          </Card>
        </Gutter>
      )}

      {!!showPassModal && (
        <EngagementPassModal
          candidate={candidateOpp.candidate}
          closeModal={handleClosePassModal}
          submitModal={handleSubmitPassModal}
        />
      )}

      {!!showInterviewPromptModal && (
        <TalentInterviewPromptModal
          candidate={candidateOpp.candidate}
          onClose={() => {
            setShowInterviewPromptModal(false);
          }}
          onBack={() => {
            setShowInterviewPromptModal(false);
          }}
          onNext={(timezoneId, description) => {
            const t = CalendarTimeZoneUtil.getZoneFromId(timezoneId);
            setFormattedPrompt(`${t.abbr} - ${t.name}<br>${description}`);
            setShowInterviewPromptModal(false);
            setShowInterviewConfirmModal(true);
          }}
        />
      )}

      {!!showInterviewConfirmModal && user ? (
        <TalentInterviewConfirmModal
          loggedInUser={user}
          submission={submission}
          candidate={candidateOpp.candidate}
          onClose={setShowInterviewConfirmModal}
          onConfirm={confirmInterview}
          primaryContactId={primaryContactId}
          availabilityNote={formattedPrompt}
        />
      ) : null}

      {showInterviewAuto && user && calendarData ? (
        <TalentInterviewScheduleModal
          user={user}
          submission={submission}
          candidate={candidateOpp.candidate}
          calendarData={calendarData}
          onNoTimeAvailableFlowConfirm={confirmInterview}
          onConfirm={scheduleInterview}
          onClose={() => {
            setShowInterviewAuto(false);
          }}
          primaryContactId={primaryContactId}
        />
      ) : null}
      {!!showEngageConfirmPrompt && (
        <EngagementPrompt
          candidateOpp={candidateOpp}
          onClose={handleEngageRequestToggle}
          onConfirm={handleEngageConfirm}
        />
      )}

      {displayLoginModal ? (
        <PublicEngagementToLoginModal
          onClose={() => {
            setDisplayLoginModal(false);
          }}
        />
      ) : null}
    </div>
  );
};
